import React from 'react';
import LegalLayout from '../components/layout/LegalLayout';

const PrivacyPage: React.FC = () => (
  <LegalLayout
    title="Privacy Policy"
    subtitle="We take your privacy seriously. This policy explains what data we collect, why we collect it, and how we protect it."
    lastUpdated="March 22, 2026"
  >
    <h2>1. Who we are</h2>
    <p>Jötr is a note-taking application operated by Jötr Inc. ("Jötr", "we", "us", or "our"). Our registered address is Lagos, Nigeria. You can reach us at <a href="mailto:privacy@Jötr.co">privacy@Jötr.co</a>.</p>

    <h2>2. What data we collect</h2>
    <h3>Data you give us</h3>
    <ul>
      <li><strong>Account information</strong> — your name and email address when you sign up.</li>
      <li><strong>Your notes</strong> — the content you write, including text, tags, and folder names.</li>
      <li><strong>Payment information</strong> — processed securely by Stripe; we never store card details.</li>
    </ul>
    <h3>Data we collect automatically</h3>
    <ul>
      <li><strong>Usage data</strong> — pages visited, features used, search queries (anonymised).</li>
      <li><strong>Device data</strong> — browser type, operating system, screen size.</li>
      <li><strong>Log data</strong> — IP address, timestamps, error logs. Retained for 30 days.</li>
    </ul>

    <h2>3. How we use your data</h2>
    <ul>
      <li>To provide and improve the Jötr service.</li>
      <li>To sync your notes across your devices.</li>
      <li>To send product updates (you can unsubscribe at any time).</li>
      <li>To detect and prevent fraud and abuse.</li>
      <li>To comply with legal obligations.</li>
    </ul>
    <p>We do <strong>not</strong> sell your personal data to third parties. We do not use your note content to train AI models.</p>

    <h2>4. How we store and protect your data</h2>
    <p>Your data is stored on servers in the EU (Amazon Web Services, Frankfurt region). All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Your notes are encrypted before storage — our staff cannot read the content of your notes.</p>

    <h2>5. Data retention</h2>
    <p>We retain your data for as long as your account is active. When you delete your account, all your notes, folders, and personal data are permanently deleted within 30 days. You can request immediate deletion by emailing <a href="mailto:privacy@Jötr.co">privacy@Jötr.co</a>.</p>

    <h2>6. Your rights</h2>
    <p>Depending on your location, you have the following rights:</p>
    <ul>
      <li><strong>Access</strong> — request a copy of your data.</li>
      <li><strong>Correction</strong> — fix inaccurate data.</li>
      <li><strong>Deletion</strong> — delete your account and all data.</li>
      <li><strong>Portability</strong> — export your notes in a standard format.</li>
      <li><strong>Objection</strong> — opt out of certain data processing.</li>
    </ul>
    <p>To exercise any of these rights, email <a href="mailto:privacy@Jötr.co">privacy@Jötr.co</a>. We will respond within 30 days.</p>

    <h2>7. Cookies</h2>
    <p>We use essential cookies to keep you logged in and remember your preferences. We also use analytics cookies if you consent. See our full <a href="/cookies">Cookie Policy</a> for details and to manage your preferences.</p>

    <h2>8. Third-party services</h2>
    <ul>
      <li><strong>Google (OAuth)</strong> — if you sign in with Google.</li>
      <li><strong>Stripe</strong> — for payment processing (Pro plan).</li>
      <li><strong>Postmark</strong> — for transactional emails.</li>
      <li><strong>Sentry</strong> — for error monitoring (anonymised).</li>
    </ul>
    <p>Each provider is bound by their own privacy policy and our data processing agreements.</p>

    <h2>9. Children</h2>
    <p>Jötr is not directed at children under the age of 13. We do not knowingly collect data from children. If you believe a child has created an account, please contact us immediately.</p>

    <h2>10. Changes to this policy</h2>
    <p>We may update this policy from time to time. We will notify you by email and display a notice in the app at least 14 days before any material changes take effect.</p>

    <h2>11. Contact</h2>
    <p>Questions about this policy? Email <a href="mailto:privacy@Jötr.co">privacy@Jötr.co</a> or write to us at Jötr Inc., Lagos, Nigeria.</p>
  </LegalLayout>
);

export default PrivacyPage;
